import './FormBuilder.scss';
import { clsx } from 'clsx';
import { UseFormReturn } from 'react-hook-form';
import { IDocumentBlock, IRows } from '@models/Forms/IForms';
import Row from './controls/Row/Row';
import { IFieldElem } from '@models/IFormData';
import { IUniqueKey } from './interfaces/IUniqueKey';
import { IDocumentScheme } from '@models/document/IDocumentScheme';
import Preloader from '@srm/ui/preloader';
import { useContext, useMemo } from 'react';
import { TableColumnSettingsContext, TableColumnSettingsUserContext } from '@/context/TableColumnSettingsContext';
import { tableColumnSettingsScope } from './tableColumnSettingsScope';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';
import { registerNestedFormBuilder } from './NestedFormBuilder/nestedFormBuilderRegistry';
import { FormMessageReserveContext } from './FormMessageReserveContext';

export interface IFormBuilder<TFieldValues extends object = object> extends IUniqueKey {
    block?: IDocumentBlock;
    rows?: IRows;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    turnOffInvokeChangesOnNew?: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
    docId?: string;
    docScheme?: IDocumentScheme;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
    /**
     * Форма вкладки карточки документа: ряды из четырёх колонок переносятся по ширине карточки, в правке и создании
     * поля держат место под строку ошибки. Панели фильтров, окна и параметры отчётов — без него.
     */
    documentCard?: boolean;
}

const FormBuilder = <TFieldValues extends object = object>({
    block,
    rows,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    includedFields,
    docId,
    uniqueKey,
    docScheme,
    ensureExternalDataSourceLoaded,
    documentCard = false,
    ...props
}: IFormBuilder<TFieldValues>) => {
    const user = useContext(TableColumnSettingsUserContext);
    const settingsRoot = docScheme?.form;
    const settingsScope = docScheme && user ? JSON.stringify([user, docScheme.flowKey, docScheme.formName]) : undefined;
    const settings = useMemo(
        () => (settingsRoot && settingsScope ? tableColumnSettingsScope(settingsRoot, settingsScope) : () => undefined),
        [settingsRoot, settingsScope],
    );
    const rowItems = block?.rows.row ?? rows?.rows.row;
    const commonRowProps = {
        fields,
        formMethods,
        docId,
        setError,
        isEdit,
        isNew,
        turnOffInvokeChangesOnNew: turnOffInvokeChangesOnNew ?? false,
        includedFields,
        uniqueKey,
        docScheme,
        ensureExternalDataSourceLoaded,
    };

    const reserveMessages = documentCard && (isEdit || isNew);

    return (
        <TableColumnSettingsContext value={settings}>
            <FormMessageReserveContext value={reserveMessages}>
                <div
                    className={clsx(
                        'form-container',
                        documentCard && 'form-container--card',
                        reserveMessages && 'form-container--edit',
                    )}
                    {...props}
                >
                    {rowItems ? (
                        rowItems.map((item, i) => <Row key={uniqueKey + i} row={item} {...commonRowProps} />)
                    ) : rows === undefined ? (
                        <Preloader size="lg" position="static" />
                    ) : null}
                </div>
            </FormMessageReserveContext>
        </TableColumnSettingsContext>
    );
};

registerNestedFormBuilder(FormBuilder);

export default FormBuilder;
